import { useApiClient } from '@eluve/api-client-provider';
import type { BulkEntityFilters } from '@eluve/api-contract';
import { BulkEntitySelectionMode as SelectionMode } from '@eluve/api-contract';
import { useIsFeatureFlagEnabled } from '@eluve/smart-blocks';

type BulkPdfZipUrlParams =
  | {
      selectionMode: typeof SelectionMode.EXPLICIT;
      appointmentIds: string[];
    }
  | {
      selectionMode: typeof SelectionMode.FILTER;
      filters: BulkEntityFilters;
    };

export const useCreateBulkPdfZipUrl = ({ tenantId }: { tenantId: string }) => {
  const api = useApiClient();
  const isBulkChartExportEnabled = useIsFeatureFlagEnabled('BULK_CHART_EXPORT');

  return async (params: BulkPdfZipUrlParams) => {
    const { selectionMode } = params;
    if (!isBulkChartExportEnabled) {
      throw new Error('Bulk chart export is not enabled');
    }

    if (selectionMode === SelectionMode.EXPLICIT) {
      if (params.appointmentIds.length === 0) {
        throw new Error('No appointment IDs provided');
      }
    } else if (selectionMode === SelectionMode.FILTER) {
      if (!params.filters) {
        throw new Error('No filters provided');
      }
    }

    const body = {
      selectionMode,
      ...(selectionMode === SelectionMode.EXPLICIT
        ? { appointmentIds: params.appointmentIds }
        : { filters: params.filters }),
      type: 'CHART' as const,
    };

    const response = await api.appointments.createBulkPdfZipUrl({
      params: {
        tenantId,
      },
      body,
    });

    if (response.status === 201) {
      return response.body;
    } else if (response.status === 403) {
      throw new Error(
        'You are not authorized to access all requested appointments.',
      );
    } else if (response.status === 422) {
      const rawMessage =
        response.body.message?.trim() ??
        'An error occurred while processing your request.';

      // Remove exception class name prefix if present (case-insensitive)
      const errorMessage = rawMessage.replace(
        /^UnprocessableEntityException:\s*/i,
        '',
      );

      throw new Error(errorMessage);
    }

    throw new Error('An unexpected error occurred.');
  };
};
